import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  defaultHeaders,
  get,
  getApiBaseUrl,
  superFetch,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { ReceiptBatchControllerResultTypes } from '@pflegenavi/service-provider-shared/api-endpoints';
import {
  ReceiptBatchControllerDefinition,
  SubmitReceiptBatchError,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import { generatePath } from '@pflegenavi/frontend/routing';

interface SubmitReceiptBatchServiceProviderInfoIncompleteException
  extends Error {
  errorCode: SubmitReceiptBatchError.SERVICE_PROVIDER_INFO_INCOMPLETE;
}

export const isSubmitReceiptBatchServiceProviderInfoIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchServiceProviderInfoIncompleteException => {
  return (
    ('errorCode' in error &&
      error.errorCode ===
        SubmitReceiptBatchError.SERVICE_PROVIDER_INFO_INCOMPLETE) ||
    // TODO: We should determine how to best structure error handling..
    // It should be possible to provide translations in the backend using gettext,
    // then we can display those directly.
    // The language could be set using a header.
    ('errors' in error &&
      'detail' in error.errors &&
      error.errors.detail?.startsWith('Invalid service provider data'))
  );
};

interface SubmitReceiptBatchServiceProviderUserIncompleteException
  extends Error {
  errorCode: SubmitReceiptBatchError.SERVICE_PROVIDER_USER_INCOMPLETE;
}

export const isSubmitReceiptBatchServiceProviderUserIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchServiceProviderUserIncompleteException => {
  return (
    'errorCode' in error &&
    error.errorCode === SubmitReceiptBatchError.SERVICE_PROVIDER_USER_INCOMPLETE
  );
};

interface SubmitReceiptBatchNursingHomeInfoIncompleteException extends Error {
  errorCode: SubmitReceiptBatchError.NURSING_HOME_INFO_INCOMPLETE;
}

export const isSubmitReceiptBatchNursingHomeInfoIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchNursingHomeInfoIncompleteException => {
  return (
    'errorCode' in error &&
    error.errorCode === SubmitReceiptBatchError.NURSING_HOME_INFO_INCOMPLETE
  );
};

interface SubmitReceiptBatchResidentInfoIncompleteException extends Error {
  errorCode: SubmitReceiptBatchError.RESIDENT_INFO_INCOMPLETE;
}

export const isSubmitReceiptBatchResidentInfoIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchResidentInfoIncompleteException => {
  return (
    'errorCode' in error &&
    error.errorCode === SubmitReceiptBatchError.RESIDENT_INFO_INCOMPLETE
  );
};

export const isSubmitReceiptBatchDateError = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): boolean => {
  return (
    'statusCode' in error &&
    error.statusCode === 422 &&
    (error.message.includes(
      'If useIndividualReceiptDates is false, receiptDate must be set'
    ) ||
      error.message.includes(
        'Failed to generate receipts. Receipt date not found.'
      ) ||
      error.message.includes(
        'Failed to generate receipts. No receipt date found.'
      ))
  );
};

export interface IReceiptBatchApi extends Api {
  getReceiptBatch(dataIn: {
    params: { batchId: string };
  }): Promise<ReceiptBatchControllerResultTypes['getReceiptBatch']>;

  generateReceiptBatchEntryPdf(dataIn: {
    params: { batchId: string; entryId: string };
  }): Promise<ReceiptBatchControllerResultTypes['generateReceiptPdf']>;

  generateReceiptBatchPdf(dataIn: {
    params: { batchId: string };
  }): Promise<ReceiptBatchControllerResultTypes['generateReceiptBatchPdf']>;
}

export class ReceiptBatchApi implements IReceiptBatchApi {
  baseUrl: string;

  getReceiptBatch: IReceiptBatchApi['getReceiptBatch'];
  generateReceiptBatchEntryPdf: IReceiptBatchApi['generateReceiptBatchEntryPdf'];
  generateReceiptBatchPdf: IReceiptBatchApi['generateReceiptBatchPdf'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getApiBaseUrl(tenantId, apiUrl);

    this.getReceiptBatch = get({
      authContext,
      url: (opts) => {
        const getReceiptBatch = generatePath(
          ReceiptBatchControllerDefinition.endpoints.getReceiptBatch.url,
          {
            batchId: opts.batchId,
          }
        );
        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${getReceiptBatch}`;
      },
    });

    this.generateReceiptBatchEntryPdf = async (dataIn) => {
      const generateReceiptPdf = generatePath(
        ReceiptBatchControllerDefinition.endpoints.generateReceiptPdf.url,
        {
          batchId: dataIn.params.batchId,
          entryId: dataIn.params.entryId,
        }
      );
      const finalUrl = `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${generateReceiptPdf}`;

      return await superFetch(authContext, finalUrl, {
        method: 'POST',
        headers: defaultHeaders(),
      }).then((res) => res.blob());
    };

    this.generateReceiptBatchPdf = async (dataIn) => {
      const generateReceiptBatchPdf = generatePath(
        ReceiptBatchControllerDefinition.endpoints.generateReceiptBatchPdf.url,
        {
          batchId: dataIn.params.batchId,
        }
      );
      const finalUrl = `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${generateReceiptBatchPdf}`;

      return await superFetch(authContext, finalUrl, {
        method: 'POST',
        headers: defaultHeaders(),
      }).then((res) => res.blob());
    };
  }
}
